/**
 * 全域 zod 錯誤訊息(繁體中文)。
 *
 * 只有在欄位「沒有」帶自訂 message 時才會用到這裡的翻譯 —— zod 對已提供
 * 明確 message 的 issue(例如 `.max(50, '...')`、`.refine(fn, { message: '...' })`)
 * 一律直接採用該 message,完全不會呼叫這個 errorMap(見 zod v3
 * `helpers/parseUtil.ts` 的 `makeIssue`:`if (issueData.message !== undefined) return ...`）。
 * 所以套用這份全域預設值不會影響現有已客製化的中文訊息。
 */
import { z, ZodIssueCode, ZodParsedType } from 'zod';

const STRING_KIND_LABELS: Record<string, string> = {
  email: '電子郵件',
  url: '網址',
  uuid: 'UUID',
  cuid: 'CUID',
  cuid2: 'CUID',
  ulid: 'ULID',
  datetime: '日期時間',
  date: '日期',
  time: '時間',
  duration: '期間',
  ip: 'IP 位址',
  base64: 'Base64',
  emoji: 'emoji',
  regex: '格式',
};

export const zhTwErrorMap: z.ZodErrorMap = (issue, ctx) => {
  switch (issue.code) {
    case ZodIssueCode.invalid_type:
      if (issue.received === ZodParsedType.undefined || issue.received === ZodParsedType.null) {
        return { message: '必填' };
      }
      return { message: '型別錯誤' };
    case ZodIssueCode.invalid_literal:
      return { message: '欄位值不正確' };
    case ZodIssueCode.unrecognized_keys:
      return { message: `不允許的欄位:${issue.keys.join('、')}` };
    case ZodIssueCode.invalid_union:
      return { message: '格式不正確' };
    case ZodIssueCode.invalid_union_discriminator:
      return { message: '欄位值不正確' };
    case ZodIssueCode.invalid_enum_value:
      return { message: `欄位值不正確(允許的值:${issue.options.join('、')})` };
    case ZodIssueCode.invalid_arguments:
    case ZodIssueCode.invalid_return_type:
      return { message: '參數不正確' };
    case ZodIssueCode.invalid_date:
      return { message: '日期格式不正確' };
    case ZodIssueCode.invalid_string: {
      const kind = typeof issue.validation === 'string' ? issue.validation : undefined;
      const label = kind ? STRING_KIND_LABELS[kind] : undefined;
      return { message: label ? `${label}格式不正確` : '格式不正確' };
    }
    case ZodIssueCode.too_small: {
      if (issue.type === 'string') {
        return { message: issue.minimum === 1 ? '必填' : `至少需 ${issue.minimum} 個字元` };
      }
      if (issue.type === 'array') return { message: `至少需 ${issue.minimum} 筆` };
      if (issue.type === 'number' || issue.type === 'bigint') {
        return { message: issue.inclusive ? `不可小於 ${issue.minimum}` : `必須大於 ${issue.minimum}` };
      }
      return { message: '數值過小' };
    }
    case ZodIssueCode.too_big: {
      if (issue.type === 'string') return { message: `最長 ${issue.maximum} 個字元` };
      if (issue.type === 'array') return { message: `最多 ${issue.maximum} 筆` };
      if (issue.type === 'number' || issue.type === 'bigint') {
        return { message: issue.inclusive ? `不可大於 ${issue.maximum}` : `必須小於 ${issue.maximum}` };
      }
      return { message: '數值過大' };
    }
    case ZodIssueCode.custom:
      return { message: '驗證失敗' };
    case ZodIssueCode.invalid_intersection_types:
      return { message: '資料格式衝突' };
    case ZodIssueCode.not_multiple_of:
      return { message: `必須是 ${issue.multipleOf} 的倍數` };
    case ZodIssueCode.not_finite:
      return { message: '數值必須為有限數' };
    default:
      return { message: ctx.defaultError };
  }
};

z.setErrorMap(zhTwErrorMap);
